// src/components/dashboard/EpisodeSelector.jsx
import { appConfirm } from '../../services/appDialogs';
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, Film, Clock, ArrowRight, ArrowLeft, BookOpen, DollarSign, ScrollText, Palette, Settings, Check, X, Loader2, Pencil } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { fetchPublicVisualReferences } from "../../services/visualReferenceService";
import { attachVisualStyleReferences, normalizeVisualStyleKey } from "../../utils/visualStyleReferences";
import { STYLE_OPTIONS } from "../storyboard/common/Constants";

const isNoFixedVisualStyle = (value) => {
  const key = String(value || "").trim().toLowerCase();
  return key === "custom" || key === "none" || key === "no_style";
};

const getProjectVisualStyleMode = (project) => {
  const mode = String(project?.visualStyleMode || "").trim().toLowerCase();
  if (mode === "fixed" && project?.visualStyle) return "fixed";
  if (mode === "none") return "none";
  return "legacy";
};

const getProjectVisualStyleValue = (project) => {
  const mode = getProjectVisualStyleMode(project);
  if (mode === "fixed") return normalizeVisualStyleKey(project.visualStyle, "modern_cdrama");
  if (mode === "none") return "custom";
  return "custom";
};

const getStyleLabel = (styleValue, mode, styleOptions = STYLE_OPTIONS) => {
  if (mode === "legacy") return "未限定风格";
  if (isNoFixedVisualStyle(styleValue)) return "无固定风格";
  return styleOptions.find((item) => item.value === styleValue)?.label
    || STYLE_OPTIONS.find((item) => item.value === styleValue)?.label
    || styleValue
    || "未设置";
};

const episodeDisplayTitle = (episode) => {
  const order = Number(episode?.sortOrder || episode?.episodeNumber || 0);
  const rawTitle = String(episode?.title || "").trim();
  if (!order) return rawTitle || "未命名集数";
  const cleanTitle = rawTitle
    .replace(/^\s*#{1,6}\s*/, "")
    .replace(/^第\s*[一二三四五六七八九十百千万零〇两\d]+\s*[集章幕回话节部]\s*[：:、.．\-—_\s]*/, "")
    .replace(/^(?:episode|ep|e)\.?\s*0*\d+\s*[：:、.．\-—_\s]*/i, "")
    .trim();
  return cleanTitle ? `第${order}集 ${cleanTitle}` : `第${order}集`;
};

const EpisodeSelector = ({ project, onProjectUpdated }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState([]);
  const [newEpTitle, setNewEpTitle] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isCreatingEpisode, setIsCreatingEpisode] = useState(false);
  const [isStyleSettingsOpen, setIsStyleSettingsOpen] = useState(false);
  const [draftStyleValue, setDraftStyleValue] = useState("custom");
  const [savingStyle, setSavingStyle] = useState(false);
  const [visualStyleReferences, setVisualStyleReferences] = useState([]);
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [projectNameDraft, setProjectNameDraft] = useState(project?.name || "");
  const [savingProjectName, setSavingProjectName] = useState(false);
  const [episodeRenameTarget, setEpisodeRenameTarget] = useState(null);
  const [episodeNameDraft, setEpisodeNameDraft] = useState("");
  const [savingEpisodeName, setSavingEpisodeName] = useState(false);

  const projectStyleMode = getProjectVisualStyleMode(project);
  const projectStyleValue = getProjectVisualStyleValue(project);
  const visualReferenceMarket = project?.projectMarket === "overseas" || project?.contentLanguage === "en-US"
    ? "overseas"
    : "domestic";
  const visualStyleOptions = useMemo(
    () => attachVisualStyleReferences(STYLE_OPTIONS, visualStyleReferences),
    [visualStyleReferences]
  );
  const projectStyleLabel = getStyleLabel(projectStyleValue, projectStyleMode, visualStyleOptions);

  const fetchEpisodes = useCallback(async () => {
    if (!project?.id) return;
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${project.id}`);
      const list = Array.isArray(data) ? [...data] : [];
      list.sort((left, right) => {
        const orderDiff = Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0);
        return orderDiff || Number(left?.id || 0) - Number(right?.id || 0);
      });
      setEpisodes(list);
    } catch (error) {
      console.error(error);
    }
  }, [project?.id]);

  useEffect(() => {
    fetchEpisodes();
  }, [fetchEpisodes]);

  useEffect(() => {
    setDraftStyleValue(projectStyleValue);
  }, [project?.id, projectStyleValue]);

  useEffect(() => {
    setProjectNameDraft(project?.name || "");
    setEditingProjectName(false);
  }, [project?.id, project?.name]);

  useEffect(() => {
    if (!isStyleSettingsOpen) return;
    let cancelled = false;
    fetchPublicVisualReferences({ category: "visual_style", mediaType: "image", market: visualReferenceMarket })
      .then((data) => {
        if (!cancelled) setVisualStyleReferences(data?.items || []);
      })
      .catch((error) => {
        console.warn("[EpisodeSelector] visual style references load failed", error);
      });
    return () => {
      cancelled = true;
    };
  }, [isStyleSettingsOpen, visualReferenceMarket]);

  const handleAdd = async () => {
    if (!newEpTitle.trim() || isCreatingEpisode) return;
    setIsCreatingEpisode(true);
    try {
      await authenticatedFetch(API_HOST + "/api/project/episode/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: project.id, title: newEpTitle }),
      });
      setNewEpTitle("");
      setIsAdding(false);
      await fetchEpisodes();
      window.dispatchEvent(new Event("episode-list-change"));
    } catch (e) {
      toast.error("创建失败: " + e.message, 0);
    } finally {
      setIsCreatingEpisode(false);
    }
  };

  const handleDelete = async (e, epId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!(await appConfirm("确定删除这一集吗？分镜数据将丢失！"))) return;
    try {
      await authenticatedFetch(`${API_HOST}/api/project/episode/delete?episodeId=${epId}`, { method: "DELETE" });
      await fetchEpisodes();
      window.dispatchEvent(new Event("episode-list-change"));
    } catch { toast.error("删除失败", 0); }
  };

  const handleSaveProjectName = async () => {
    const nextName = projectNameDraft.trim();
    if (!nextName) {
      toast.warning("项目名称不能为空");
      return;
    }
    if (nextName === String(project?.name || "").trim()) {
      setEditingProjectName(false);
      return;
    }
    try {
      setSavingProjectName(true);
      const updated = await authenticatedFetch(`${API_HOST}/api/project/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: Number(project.id), name: nextName }),
      });
      const nextProject = { ...project, ...(updated?.data || updated || {}), name: nextName };
      onProjectUpdated?.(nextProject);
      window.dispatchEvent(new CustomEvent("project-runtime-profile-change", {
        detail: { projectId: project.id, project: nextProject },
      }));
      setEditingProjectName(false);
      toast.success("项目名称已修改");
    } catch (error) {
      toast.error(`项目名称修改失败: ${error.message || error}`, 0);
    } finally {
      setSavingProjectName(false);
    }
  };

  const openEpisodeRename = (event, episode) => {
    event.preventDefault();
    event.stopPropagation();
    setEpisodeRenameTarget(episode);
    setEpisodeNameDraft(episode?.title || "");
  };

  const handleSaveEpisodeName = async () => {
    const nextName = episodeNameDraft.trim();
    if (!episodeRenameTarget?.id || !nextName) {
      toast.warning("集数名称不能为空");
      return;
    }
    if (nextName === String(episodeRenameTarget.title || "").trim()) {
      setEpisodeRenameTarget(null);
      return;
    }
    try {
      setSavingEpisodeName(true);
      const saved = await authenticatedFetch(`${API_HOST}/api/project/episode/update-title`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ episodeId: Number(episodeRenameTarget.id), title: nextName }),
      });
      setEpisodes((current) => current.map((episode) => (
        Number(episode.id) === Number(episodeRenameTarget.id)
          ? { ...episode, ...saved }
          : episode
      )));
      window.dispatchEvent(new Event("episode-list-change"));
      setEpisodeRenameTarget(null);
      toast.success("集数名称已修改");
    } catch (error) {
      toast.error(`集数名称修改失败: ${error.message || error}`, 0);
    } finally {
      setSavingEpisodeName(false);
    }
  };

  const handleSaveProjectStyle = async () => {
    if (!project?.id || savingStyle) return;
    const nextMode = isNoFixedVisualStyle(draftStyleValue) ? "none" : "fixed";
    const nextVisualStyle = nextMode === "fixed" ? draftStyleValue : null;
    const nextLabel = getStyleLabel(draftStyleValue, nextMode, visualStyleOptions);

    try {
      setSavingStyle(true);
      const updated = await authenticatedFetch(`${API_HOST}/api/project/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId: Number(project.id),
          visualStyleMode: nextMode,
          visualStyle: nextVisualStyle,
        }),
      });
      const responseProject = updated?.data || updated || {};
      const mergedProject = {
        ...project,
        ...responseProject,
        visualStyleMode: nextMode,
        visualStyle: nextVisualStyle,
      };
      onProjectUpdated?.(mergedProject);
      try {
        localStorage.setItem(`project_runtime_profile_${project.id}`, JSON.stringify(mergedProject));
        window.dispatchEvent(new CustomEvent("project-runtime-profile-change", {
          detail: { projectId: project.id, project: mergedProject },
        }));
      } catch {
        // 本地缓存失败不影响后端保存。
      }
      setIsStyleSettingsOpen(false);
      toast.success(`项目画风已保存为 ${nextLabel}`);
    } catch (error) {
      toast.error(`项目画风保存失败: ${error.message || error}`, 0);
    } finally {
      setSavingStyle(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col font-sans selection:bg-[var(--color-accent-light)]" style={{ backgroundColor: 'var(--color-bg-primary)' }}>

      {/* 顶部工具栏 */}
      <div className="h-14 border-b flex items-center justify-between px-6 z-10 shrink-0" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-primary)' }}>
        <div className="flex items-center gap-4">
            <Link
                to="/"
                className="p-1.5 rounded-lg transition-all duration-200 cursor-pointer"
                style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}
                title="返回项目列表"
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-hover)'; e.currentTarget.style.color = 'var(--color-text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-primary)'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
            >
                <ArrowLeft size={16}/>
            </Link>

            <div className="h-4 w-px" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

            <div className="min-w-0">
              {editingProjectName ? (
                <div className="flex items-center gap-2">
                  <Film style={{ color: theme.colors.bluePrimary }} size={16} className="shrink-0" />
                  <input
                    value={projectNameDraft}
                    onChange={(event) => setProjectNameDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleSaveProjectName();
                      if (event.key === "Escape") {
                        setProjectNameDraft(project?.name || "");
                        setEditingProjectName(false);
                      }
                    }}
                    disabled={savingProjectName}
                    maxLength={180}
                    autoFocus
                    className="h-8 w-[min(360px,38vw)] rounded-md border px-2 text-sm font-semibold outline-none"
                    style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: theme.colors.bluePrimary, color: "var(--color-text-primary)" }}
                  />
                  <button
                    type="button"
                    onClick={handleSaveProjectName}
                    disabled={savingProjectName}
                    className="flex size-8 items-center justify-center rounded-md cursor-pointer disabled:opacity-50"
                    style={{ color: "var(--color-success)", backgroundColor: "var(--color-bg-tertiary)" }}
                    title="保存项目名称"
                  >
                    {savingProjectName ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setProjectNameDraft(project?.name || "");
                      setEditingProjectName(false);
                    }}
                    disabled={savingProjectName}
                    className="flex size-8 items-center justify-center rounded-md cursor-pointer disabled:opacity-50"
                    style={{ color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}
                    title="取消"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="group/project-name flex min-w-0 items-center gap-2">
                  <h1 className="min-w-0 truncate font-bold text-lg flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                    <Film style={{ color: theme.colors.bluePrimary }} size={16} className="shrink-0" /> {project.name}
                  </h1>
                  <button
                    type="button"
                    onClick={() => setEditingProjectName(true)}
                    className="flex size-7 shrink-0 items-center justify-center rounded-md opacity-60 transition hover:opacity-100 cursor-pointer"
                    style={{ color: "var(--color-text-muted)" }}
                    title="修改项目名称"
                  >
                    <Pencil size={13} />
                  </button>
                </div>
              )}
            </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (episodes.length === 0) { toast.warning("请先创建集数"); return; }
              navigate(`/project/${project.id}/episode/${episodes[0].id}/mini-storyboard`);
            }}
            className="flex items-center gap-2 px-4 py-1.5 rounded-full font-medium transition-colors duration-150 text-xs cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
          >
            <ScrollText size={14}/> <span className="hidden md:inline">小故事板</span>
          </button>
          <button
            onClick={() => navigate(`/project/${project.id}/cost`)}
            className="flex items-center gap-2 px-4 py-1.5 rounded-full font-medium transition-colors duration-150 text-xs cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
          >
            <DollarSign size={14}/> <span className="hidden md:inline">费用统计</span>
          </button>
          <button
            onClick={() => navigate(`/project/${project.id}/outline`)}
            className="flex items-center gap-2 px-4 py-1.5 rounded-full font-medium transition-colors duration-150 text-xs cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
          >
            <BookOpen size={14}/> <span className="hidden md:inline">剧情大纲</span>
          </button>
          <button onClick={() => setIsAdding(true)} className="flex items-center gap-2 px-4 py-1.5 rounded-full font-medium transition-opacity duration-150 text-xs cursor-pointer text-[var(--color-accent-contrast)] bg-[var(--color-accent-primary)] hover:opacity-90">
              <Plus size={14}/> <span className="hidden md:inline">新建集数</span>
          </button>
        </div>
      </div>

      <div className="flex-1 p-8 max-w-5xl mx-auto w-full overflow-y-auto custom-scrollbar z-10">
        <div
          className="mb-6 rounded-xl p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
          style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-secondary)' }}
            >
              <Palette size={18} style={{ color: 'var(--color-text-muted)' }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>项目画风</span>
                <span
                  className="px-2 py-0.5 rounded-full text-2xs font-bold"
                  style={{ color: theme.colors.purplePrimary, backgroundColor: `${theme.colors.purplePrimary}12` }}
                >
                  {projectStyleLabel}
                </span>
              </div>
              <p className="text-xs mt-1 truncate" style={{ color: 'var(--color-text-muted)' }}>
                {projectStyleMode === "legacy"
                  ? "按创作需要选择一个稳定画风。"
                  : "当前项目会使用这个画风。"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsStyleSettingsOpen(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-colors duration-150 cursor-pointer shrink-0 border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
          >
            <Settings size={14} />
            修改项目画风
          </button>
        </div>

        {/* 新建面板 */}
        {isAdding && (
            <div className="mb-6 p-6 rounded-xl flex flex-col md:flex-row gap-4 animate-in fade-in slide-in-from-top-2 shadow-lg" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <input
                    className="flex-1 rounded-lg px-4 py-3 outline-none transition-all duration-200 text-sm"
                    style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                    placeholder="集数标题 (如: 第一集 决战紫禁之巅)"
                    value={newEpTitle}
                    onChange={e => setNewEpTitle(e.target.value)}
                    disabled={isCreatingEpisode}
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 2px var(--color-accent-glow)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-primary)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
                <div className="flex gap-3">
                    <button disabled={isCreatingEpisode} onClick={() => setIsAdding(false)} className="px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" style={{ color: 'var(--color-text-muted)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-text-primary)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}>
                      取消
                    </button>
                    <button disabled={isCreatingEpisode || !newEpTitle.trim()} onClick={handleAdd} className="px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-200 cursor-pointer text-[var(--color-accent-contrast)] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2" style={{ backgroundColor: theme.colors.accentPrimary }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 4px 12px ${theme.colors.accentGlow}`; e.currentTarget.style.filter = 'brightness(1.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.filter = 'none'; }}>
                      {isCreatingEpisode && <Loader2 size={14} className="animate-spin" />}
                      {isCreatingEpisode ? "创建中" : "确定创建"}
                    </button>
                </div>
            </div>
        )}

        {/* 列表 */}
        <div className="space-y-4 pb-20">
            {episodes.map((ep) => (
                <Link
                    key={ep.id}
                    to={`/project/${project.id}/episode/${ep.id}/mini-storyboard`}
                    className="group flex items-center justify-between p-4 rounded-xl cursor-pointer transition-[border-color,box-shadow] duration-200 block overflow-hidden relative border border-[var(--color-border-secondary)] hover:border-[color-mix(in_srgb,var(--color-blue-primary)_50%,transparent)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.2)]"
                    style={{ backgroundColor: 'var(--color-bg-card)' }}
                >
                    <div className="flex items-center gap-5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-mono font-bold text-sm shadow-inner" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
                            {ep.sortOrder}
                        </div>
                        <div className="min-w-0 flex-1">
                            <h3 className="font-bold text-base transition-all duration-200 truncate" style={{ color: 'var(--color-text-primary)' }} title={episodeDisplayTitle(ep)}>{episodeDisplayTitle(ep)}</h3>
                            <div className="flex items-center gap-2 text-2xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                                <Clock size={10}/>
                                <span>最后编辑: {new Date(ep.createdAt).toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* 右侧操作按钮区 - 固定宽度容器，悬停淡入（不占布局宽度） */}
                    <div className="flex items-center justify-end shrink-0 w-[168px]">
                         <div className="flex items-center gap-2 whitespace-nowrap opacity-0 translate-x-2 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0">
                           <span className="text-xs font-medium" style={{ color: theme.colors.bluePrimary }}>进入工作区</span>
                           <ArrowRight size={14} style={{ color: theme.colors.bluePrimary }}/>
                         </div>
                         <button
                            onClick={(e) => openEpisodeRename(e, ep)}
                            className="p-2 rounded-full transition-colors duration-150 cursor-pointer shrink-0 opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
                            title="修改集数名称"
                        >
                            <Pencil size={15}/>
                        </button>
                         <button
                            onClick={(e) => handleDelete(e, ep.id)}
                            className="p-2 rounded-full transition-colors duration-150 cursor-pointer shrink-0 opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 text-[var(--color-text-muted)] hover:bg-[var(--color-error)] hover:text-[var(--color-accent-contrast)]"
                            title="删除此集"
                        >
                            <Trash2 size={16}/>
                        </button>
                    </div>
                </Link>
            ))}

            {episodes.length === 0 && !isAdding && (
                <div className="flex flex-col items-center justify-center py-32">
                    <Film size={48} className="mb-4 opacity-20" style={{ color: 'var(--color-text-muted)' }}/>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>暂无集数，请点击右上角新建。</p>
                </div>
            )}
        </div>
      </div>

      {episodeRenameTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-in fade-in duration-150"
          style={{ backgroundColor: "var(--overlay-medium)" }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !savingEpisodeName) setEpisodeRenameTarget(null);
          }}
        >
          <div
            className="w-full max-w-md rounded-xl p-5 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-bold" style={{ color: "var(--color-text-primary)" }}>修改集数名称</h2>
                <p className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>第 {episodeRenameTarget.sortOrder} 集</p>
              </div>
              <button
                type="button"
                onClick={() => setEpisodeRenameTarget(null)}
                disabled={savingEpisodeName}
                className="flex size-8 items-center justify-center rounded-md cursor-pointer disabled:opacity-50"
                style={{ color: "var(--color-text-muted)" }}
              >
                <X size={16} />
              </button>
            </div>
            <input
              value={episodeNameDraft}
              onChange={(event) => setEpisodeNameDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleSaveEpisodeName();
                if (event.key === "Escape" && !savingEpisodeName) setEpisodeRenameTarget(null);
              }}
              maxLength={180}
              disabled={savingEpisodeName}
              autoFocus
              className="h-11 w-full rounded-lg border px-3 text-sm outline-none"
              style={{ backgroundColor: "var(--color-bg-overlay)", borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)" }}
            />
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEpisodeRenameTarget(null)}
                disabled={savingEpisodeName}
                className="h-9 rounded-md px-4 text-sm font-semibold cursor-pointer disabled:opacity-50"
                style={{ color: "var(--color-text-secondary)" }}
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSaveEpisodeName}
                disabled={savingEpisodeName || !episodeNameDraft.trim()}
                className="inline-flex h-9 items-center gap-2 rounded-md px-4 text-sm font-bold text-[var(--color-accent-contrast)] cursor-pointer disabled:opacity-50"
                style={{ backgroundColor: theme.colors.accentPrimary }}
              >
                {savingEpisodeName ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {isStyleSettingsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-in fade-in duration-150"
          style={{ backgroundColor: 'var(--overlay-medium)' }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsStyleSettingsOpen(false);
          }}
        >
          <div
	            className="w-full max-w-3xl max-h-[94vh] rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}
          >
            <div className="px-6 py-5 flex items-start justify-between border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
              <div>
                <div className="flex items-center gap-2">
                  <Palette size={18} style={{ color: theme.colors.purplePrimary }} />
                  <h2 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>修改项目画风</h2>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  项目画风保存到后端。修改后只影响后续生成，不会自动覆盖已有角色、场景、小故事板或视频。
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsStyleSettingsOpen(false)}
                className="p-2 rounded-lg cursor-pointer"
                style={{ color: 'var(--color-text-muted)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6">
              <div
                className="mb-4 rounded-xl px-4 py-3 text-xs leading-relaxed"
                style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)', color: 'var(--color-text-muted)' }}
              >
                当前状态：
                <span className="font-bold ml-1" style={{ color: theme.colors.purplePrimary }}>{projectStyleLabel}</span>
                {projectStyleMode === "legacy" && (
                  <span className="ml-2">如果不想限定画面风格，请选择“无固定风格”。</span>
                )}
              </div>

              <div className="flex flex-col gap-3 overflow-y-auto custom-scrollbar pr-1" style={{ maxHeight: "64vh" }}>
                {visualStyleOptions.map((style) => {
                  const selected = draftStyleValue === style.value;
                  const label = style.value === "custom" ? "无固定风格" : style.label;
                  const isNoStyle = style.value === "custom";
	                  return (
	                    <button
	                      type="button"
	                      key={style.value}
	                      onClick={() => setDraftStyleValue(style.value)}
	                      className={`${isNoStyle ? "px-4 py-3" : "relative"} rounded-xl text-left transition-all duration-200 cursor-pointer overflow-hidden`}
	                      style={{
	                        display: "block",
	                        width: "100%",
	                        minHeight: isNoStyle ? 56 : 72,
	                        flexShrink: 0,
	                        boxSizing: "border-box",
	                        backgroundColor: selected ? `${theme.colors.purplePrimary}14` : 'var(--color-bg-card)',
	                        border: `1px solid ${selected ? theme.colors.purplePrimary : 'var(--color-border-secondary)'}`,
	                        boxShadow: selected ? `0 0 0 1px ${theme.colors.purplePrimary}22` : 'none',
	                      }}
	                    >
                      {isNoStyle ? (
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-bold truncate" style={{ color: selected ? theme.colors.purplePrimary : 'var(--color-text-primary)' }}>
                            {label}
                          </div>
                          {selected && <Check size={16} style={{ color: theme.colors.purplePrimary }} className="shrink-0" />}
                        </div>
                      ) : (
	                        <>
	                          {style.referenceImageUrl && (
	                            <div className="relative w-full" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
	                              <img
	                                src={style.referenceImageUrl}
	                                alt={`${label}参考图`}
	                                loading="lazy"
	                                className="block w-full h-auto"
	                              />
	                            </div>
	                          )}
	                          {!style.referenceImageUrl && (
	                            <div className="relative w-full h-32" style={{ backgroundColor: 'var(--color-bg-tertiary)' }} />
	                          )}
                          <div
                            className="absolute inset-x-0 bottom-0 px-4 py-3 flex items-center justify-between gap-3"
                            style={{
                              backgroundColor: selected ? "color-mix(in srgb, var(--color-purple-primary) 58%, transparent)" : "var(--overlay-light)",
                              backdropFilter: "blur(14px)",
                              WebkitBackdropFilter: "blur(14px)",
                              borderTop: "1px solid color-mix(in srgb, var(--color-accent-contrast) 12%, transparent)"
                            }}
                          >
                            <div className="text-sm font-bold truncate text-white">
                              {label}
                            </div>
                            {selected && <Check size={16} className="shrink-0 text-white" />}
                          </div>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="px-6 py-4 flex items-center justify-end gap-3 border-t" style={{ borderColor: 'var(--color-border-primary)' }}>
              <button
                type="button"
                onClick={() => setIsStyleSettingsOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer"
                style={{ color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-secondary)' }}
              >
                取消
              </button>
              <button
                type="button"
                disabled={savingStyle}
                onClick={handleSaveProjectStyle}
                className="px-5 py-2 rounded-lg text-sm font-bold text-[var(--color-accent-contrast)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: theme.colors.purplePrimary }}
              >
                {savingStyle ? "保存中..." : "保存项目画风"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EpisodeSelector;
